import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Bell, Globe, X } from 'lucide-react';
import './CandleLightingSharePage.css';
import config from '../config';
import candleLightingMetrics from '../utils/candleLightingMetrics';
import { trackFBEvent, FB_EVENTS } from '../utils/facebookPixel';

function CandleLightingSharePage() {
  const { share_code, city } = useParams();
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [countdown, setCountdown] = useState('');
  const [isSpecialCity, setIsSpecialCity] = useState(false);
  const audioRef = useRef(null);
  const pageLoadStart = useRef(Date.now());
  const countdownLoggedRef = useRef(false);

  useEffect(() => {
    // Wait for share_code to be available from URL params
    if (share_code) {
      fetchShareData();
    }
  }, [share_code]);

  useEffect(() => {
    if (shareData?.image_urls?.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => {
          const newIndex = (prev + 1) % shareData.image_urls.length;
          candleLightingMetrics.logImageCarousel('auto_advance', newIndex);
          return newIndex;
        });
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [shareData]);

  useEffect(() => {
    if (shareData) {
      updateMetaTags();
    }
  }, [shareData]);

  // Track modal open/close
  useEffect(() => {
    if (showReminderModal) {
      candleLightingMetrics.logModalOpen('reminder_modal');
      
      // Track with Facebook Pixel - User showing interest in setting reminder
      trackFBEvent(FB_EVENTS.INITIATE_CHECKOUT, {
        content_name: 'Shabbat Reminder Interest',
        content_category: 'Candle Lighting',
        city: shareData?.city || city,
      });
    } else if (shareData) { // Only log close if modal was previously open and page is loaded
      candleLightingMetrics.logModalClose('reminder_modal');
    }
  }, [showReminderModal]);

  // Log countdown view when it becomes visible (only once)
  useEffect(() => {
    if (isSpecialCity && countdown && !countdownLoggedRef.current) {
      candleLightingMetrics.logCountdownView();
      countdownLoggedRef.current = true;
    }
  }, [isSpecialCity, countdown]);

  // Countdown timer (only for special cities)
  useEffect(() => {
    if (!isSpecialCity || !shareData?.candle_lighting || !shareData?.date || !shareData?.timezone) {
      console.log('Countdown not running:', { isSpecialCity, hasTime: !!shareData?.candle_lighting, hasDate: !!shareData?.date, hasTimezone: !!shareData?.timezone });
      return;
    }

    const updateCountdown = () => {
      try {
        const dateStr = shareData.date; // e.g., "Friday, January 31, 2026"
        const timeStr = shareData.candle_lighting; // e.g., "5:02 PM"
        
        // Determine timezone based on city
        let targetTimezone;
        if (city === 'new-york') {
          targetTimezone = 'America/New_York';
        } else if (city === 'los-angeles') {
          targetTimezone = 'America/Los_Angeles';
        } else {
          targetTimezone = shareData.timezone || 'America/Los_Angeles';
        }
        
        console.log('Calculating countdown for:', { dateStr, timeStr, timezone: targetTimezone, city });
        
        // Parse time
        const timeMatch = timeStr.match(/(\d+):(\d+)/);
        if (!timeMatch) {
          console.error('Could not parse time:', timeStr);
          return;
        }
        
        const [hours, minutes] = timeMatch.slice(1).map(Number);
        const isPM = timeStr.toLowerCase().includes('pm');
        const hour24 = isPM && hours !== 12 ? hours + 12 : hours === 12 && !isPM ? 0 : hours;
        
        // Parse date - can be "Friday, February 6" or "Friday, February 6, 2026"
        let dateMatch = dateStr.match(/([A-Za-z]+),\s+([A-Za-z]+)\s+(\d+),\s+(\d+)/);
        let monthName, day, year;
        
        if (dateMatch) {
          // Has year: "Friday, February 6, 2026"
          [, , monthName, day, year] = dateMatch;
        } else {
          // No year: "Friday, February 6"
          dateMatch = dateStr.match(/([A-Za-z]+),\s+([A-Za-z]+)\s+(\d+)/);
          if (!dateMatch) {
            console.error('Could not parse date:', dateStr);
            return;
          }
          [, , monthName, day] = dateMatch;
          // Use current year or next year if the date has passed
          const currentYear = new Date().getFullYear();
          year = currentYear;
        }
        const monthMap = {
          'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
          'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
        };
        const month = monthMap[monthName];
        
        if (month === undefined) {
          console.error('Could not find month:', monthName);
          return;
        }
        
        // SIMPLE: Parse time in target timezone
        // Create ISO string: "2026-02-06T17:11:00"
        const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour24).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
        
        // This is the LOCAL time in the target timezone. Now convert to UTC.
        // Use a known UTC time and measure the offset
        const dummyDate = new Date('2026-01-01T12:00:00Z');
        const offsetMs = dummyDate.getTime() - new Date(dummyDate.toLocaleString('en-US', { timeZone: targetTimezone })).getTime();
        
        // Parse the target time as if it were local, then apply the offset
        const targetAsLocal = new Date(dateString);
        const finalTarget = new Date(targetAsLocal.getTime() + offsetMs);
        
        const now = new Date();
        
        // Calculate difference
        const diff = finalTarget - now;
        
        console.log('Countdown diff (ms):', diff, 'Target:', finalTarget, 'Now:', now, 'Timezone:', targetTimezone);
        
        if (diff <= 0) {
          setCountdown('Lighting time has passed');
          return;
        }

        const hours_left = Math.floor(diff / (1000 * 60 * 60));
        const minutes_left = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds_left = Math.floor((diff % (1000 * 60)) / 1000);

        if (hours_left > 0) {
          setCountdown(`${hours_left}h ${minutes_left}m ${seconds_left}s`);
        } else if (minutes_left > 0) {
          setCountdown(`${minutes_left}m ${seconds_left}s`);
        } else {
          setCountdown(`${seconds_left}s`);
        }
      } catch (err) {
        console.error('Error calculating countdown:', err);
        setCountdown('');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [shareData, isSpecialCity]);

  const fetchShareData = async () => {
    // Ensure we have a share_code before making API call
    if (!share_code) {
      console.log('⏳ Waiting for share_code from URL params...');
      return;
    }

    try {
      // Check if this is a special city page based on URL parameter
      if (city === 'los-angeles' || city === 'new-york') {
        setIsSpecialCity(true);
      }

      // Always fetch data from the database using share_code
      const response = await fetch(`${config.API_URL}/shabbat-share/${share_code}`);
      
      if (!response.ok) {
        console.error('❌ API response not ok:', response.status);
        throw new Error('Share not found');
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        // Add timezone to data if this is a special city page
        const dataWithTimezone = {
          ...result.data,
          timezone: city === 'los-angeles' ? 'America/Los_Angeles' : 
                   city === 'new-york' ? 'America/New_York' : null
        };
        setShareData(dataWithTimezone);
        
        // Log page view with internal analytics
        const loadTime = Date.now() - pageLoadStart.current;
        candleLightingMetrics.setPageLoadTime(loadTime);
        candleLightingMetrics.logPageView(share_code);
        
        // Track with Facebook Pixel - ViewContent standard event
        trackFBEvent(FB_EVENTS.VIEW_CONTENT, {
          content_name: 'Candle Lighting Share Page',
          content_category: 'Shabbat',
          content_type: 'shabbat_candle_lighting',
          city: dataWithTimezone.city,
          share_code: share_code,
        });
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching share:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateMetaTags = () => {
    // Use thumbnail endpoint for og:image to show text overlays in social media previews
    const baseUrl = window.location.origin;
    const thumbnailUrl = `${baseUrl}/api/shabbat-share/${share_code}/thumbnail`;
    const sharePageUrl = `${baseUrl}/share/candle-lighting/${share_code}`;
    const title = `Shabbat Shalom from ${shareData.city}`;
    const description = `Light candles at ${shareData.candle_lighting}. Tap to set an automatic reminder. Join 1,000s lighting on time.`;
    
    document.title = title;
    
    const metaTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: thumbnailUrl },
      { property: 'og:url', content: sharePageUrl },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: thumbnailUrl },
    ];

    metaTags.forEach(({ property, name, content }) => {
      const selector = property ? `meta[property="${property}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) meta.setAttribute('property', property);
        if (name) meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    });
  };

  const toggleMusic = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      candleLightingMetrics.logMusicStop('audio_paused');
    } else {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          candleLightingMetrics.logMusicPlay();
        })
        .catch(error => {
          console.error('Error playing audio:', error);
          alert('Unable to play audio. Please check your connection and try again.');
        });
    }
  };

  const getAudioUrl = () => {
    if (!shareData?.song_name) return null;
    const songName = shareData.song_name;
    
    // If already a full URL, return as is
    if (songName.startsWith('http')) {
      return songName;
    }
    
    // Build absolute URL with API baseURL
    return `${config.API_URL}${songName.startsWith('/') ? songName : `/audio/${songName}`}`;
  };

  const getMusicName = () => {
    return `Shabbat Shalom from ${shareData?.city || 'Your Community'}`;
  };

  const getInstallAppLink = () => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    if (isIOS) {
      return 'https://apps.apple.com/us/app/yesod/id6748472269';
    } else if (isAndroid) {
      return 'https://play.google.com/store/apps/details?id=us.yesod.app';
    }
    // Default to iOS if desktop/unknown
    return 'https://apps.apple.com/us/app/yesod/id6748472269';
  };

  const handleShare = async (type) => {
    const shareUrl = `https://yesod.us/share/candle-lighting/${share_code}`;
    const shareText = `Shabbat Shalom from ${shareData.city}! 🕯️\n\nLight candles at ${shareData.candle_lighting}${shareData.parsha ? `\n${shareData.parsha}` : ''}`;

    // Log share action with internal analytics
    candleLightingMetrics.logShareAction(type);
    
    // Track with Facebook Pixel - Share is a key engagement event
    trackFBEvent(FB_EVENTS.SHARE_ACTION, {
      content_name: 'Candle Lighting Share',
      content_type: 'shabbat_candle_lighting',
      method: type, // 'whatsapp-status', 'whatsapp-message', 'copy'
      city: shareData.city,
    });

    if (type === 'whatsapp-status') {
      // Share URL link to WhatsApp Status
      const fullText = `${shareText}\n\n${shareUrl}`;
      const encodedText = encodeURIComponent(fullText);
      
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isMobile = isIOS || isAndroid;
      
      if (isMobile) {
        if (isIOS) {
          window.location.href = `whatsapp://status?text=${encodedText}`;
        } else if (isAndroid) {
          window.location.href = `intent://send?text=${encodedText}#Intent;scheme=whatsapp;package=com.whatsapp;component=com.whatsapp/.status.playback.StatusPlaybackActivity;end`;
        }
      } else {
        // Desktop: Open in WhatsApp Web
        window.open(`https://wa.me/?text=${encodedText}`, '_blank');
      }
      showCopySnackbar();
    } else if (type === 'whatsapp-message') {
      const fullText = `${shareText}\n\n${shareUrl}`;
      const encodedText = encodeURIComponent(fullText);
      window.open(`https://wa.me/?text=${encodedText}`, '_blank');
      showCopySnackbar();
    } else if (type === 'more') {
      // Check if mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile && navigator.share) {
        // Mobile: Use native share sheet
        try {
          await navigator.share({
            title: 'Shabbat Shalom',
            text: shareText,
            url: shareUrl,
          });
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('Share failed:', err);
          }
        }
      } else {
        // Desktop: Copy to clipboard
        try {
          await navigator.clipboard.writeText(shareUrl);
          showCopySnackbar();
        } catch (err) {
          console.error('Clipboard failed:', err);
        }
      }
    }
  };

  const showCopySnackbar = () => {
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 2400);
  };

  if (loading) {
    return (
      <div className="sharing-modal-container">
        <div className="content-gradient">
          <div className="scroll-content">
            {/* Logo Skeleton */}
            <div className="flyer-logo-container">
              <div className="skeleton skeleton-logo"></div>
            </div>

            {/* Card Skeleton */}
            <div className="flyer-card-container">
              <div className="flyer-card">
                <div className="skeleton skeleton-card"></div>
              </div>
            </div>

            {/* Title Skeleton */}
            <div className="share-title-section">
              <div className="skeleton skeleton-title"></div>
            </div>

            {/* Buttons Skeleton */}
            <div className="sharing-options">
              <div className="skeleton skeleton-button"></div>
              <div className="skeleton skeleton-button"></div>
              <div className="skeleton skeleton-button"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !shareData) {
    return (
      <div className="candle-lighting-share-page">
        <div className="error-container">
          <h1>😔 Oops!</h1>
          <p>Share link not found</p>
        </div>
      </div>
    );
  }

  const currentImage = shareData.image_urls?.[currentImageIndex] || 'https://yesod.us/yesod-logo.png';

  return (
    <div className="sharing-modal-container">
      <div className="content-gradient">
        {/* Sticky Header */}
        <div className="sticky-header">
          <img 
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/WhiteLogo.png" 
            alt="Yesod" 
            className="header-logo"
          />
          <button
            onClick={() => {
              candleLightingMetrics.logButtonClick('set_reminder_header');
              setShowReminderModal(true);
            }}
            className="install-app-button"
          >
            Set Reminder
            <Bell size={18} />
          </button>
        </div>

        {/* Live Network Status */}
        <div className="live-network-status">
          <div className="network-stats">
            <Globe size={16} className="globe-icon" />
            {isSpecialCity ? (
              <div className="text-with-dot">
                <span>{city === 'new-york' ? '165,000' : '86,412'} people in {shareData?.city || 'this city'} are lighting Shabbat candles {new Date().getDay() === 5 ? 'today' : 'this Friday'}</span>
                <span className="live-dot"></span>
              </div>
            ) : (
              <div className="text-with-dot">
                <span>1,000s are lighting Shabbat candles {new Date().getDay() === 5 ? 'today on time' : 'on time this Friday'}</span>
                <span className="live-dot"></span>
              </div>
            )}
          </div>
        </div>

        <div className="scroll-content">

          <div className="flyer-card-container">
            <div className="flyer-card">
              {/* Background Image Carousel */}
              <div className="image-carousel">
                <img 
                  src={currentImage} 
                  alt="Background" 
                  className="image-slide"
                />
                <div className="flyer-gradient"></div>
              </div>

              {/* City Tag */}
              <div className="flyer-city-tag">
                <svg className="map-pin-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span className="flyer-city-text">{shareData.city}</span>
              </div>

              {/* Main Content */}
              <div className="flyer-main-content">
                <div className="flyer-date">{shareData.date}</div>
                <h1 className="flyer-title">Shabbat Shalom</h1>
                {shareData.parsha && (
                  <div className="flyer-portion">{shareData.parsha}</div>
                )}

                <div className="flyer-time-container">
                  <div className="flyer-time-label">Light Candles at</div>
                  <div className="flyer-main-time">{shareData.candle_lighting}</div>
                </div>

                {shareData.havdalah && (
                  <div className="flyer-subtime">Shabbat ends {shareData.havdalah}</div>
                )}
              </div>

              {/* Music Player */}
              {shareData.song_name && (
                <div className="flyer-music-player" onClick={toggleMusic}>
                  <div className="flyer-player-icon">
                    {isPlaying ? (
                      <div className="flyer-pause-icon">
                        <div className="flyer-pause-bar"></div>
                        <div className="flyer-pause-bar"></div>
                      </div>
                    ) : (
                      <div className="flyer-play-icon"></div>
                    )}
                  </div>
                  <div className="flyer-music-text-container">
                    <div className="flyer-music-text-wrapper">
                      <span className="flyer-music-text">{getMusicName()}</span>
                      <span className="flyer-music-text">{getMusicName()}</span>
                    </div>
                  </div>
                  <div className="flyer-waveform-container">
                    {[0, 1, 2, 3].map(i => (
                      <div 
                        key={i} 
                        className={`flyer-wave-bar ${isPlaying ? 'playing' : ''}`}
                        style={{ animationDelay: `${i * 0.1}s` }}
                      ></div>
                    ))}
                  </div>
                  <audio 
                    ref={audioRef} 
                    src={getAudioUrl()} 
                    loop 
                    crossOrigin="anonymous"
                    preload="auto"
                    onError={(e) => {
                      console.error('Audio loading error:', e.target.error);
                      console.error('Audio source:', getAudioUrl());
                      console.error('Raw song_name:', shareData.song_name);
                    }}
                    onCanPlay={() => console.log('Audio ready to play:', getAudioUrl())}
                  />
                </div>
              )}

              {/* Page Indicators */}
              {shareData.image_urls?.length > 1 && (
                <div className="page-indicator-container">
                  {shareData.image_urls.map((_, index) => (
                    <div 
                      key={index} 
                      className={`page-indicator ${index === currentImageIndex ? 'page-indicator-active' : ''}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Countdown Timer (only for special cities) */}
          {isSpecialCity && countdown && (
            <div className="countdown-section">
              <div className="countdown-label">Candle Lighting In</div>
              <div className="countdown-timer">{countdown}</div>
              <button 
                className="countdown-bell-icon" 
                onClick={() => {
                  candleLightingMetrics.logButtonClick('bell_icon_countdown');
                  setShowReminderModal(true);
                }}
                aria-label="Set Reminder"
              >
                <Bell size={20} />
              </button>
            </div>
          )}

          {/* Share Title */}
          <div className="share-title-section">
            <h2 className="share-title">Share this week's Shabbat times</h2>
          </div>

          {/* Sharing Options */}
          <div className="sharing-options">
            <div className="sharing-option" onClick={() => handleShare('whatsapp-status')}>
              <div className="sharing-icon-circle">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#25D366">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
              </div>
              <span className="sharing-option-text">Status</span>
            </div>

            <div className="sharing-option" onClick={() => handleShare('whatsapp-message')}>
              <div className="sharing-icon-circle">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </div>
              <span className="sharing-option-text">Message</span>
            </div>

            <div className="sharing-option" onClick={() => handleShare('more')}>
              <div className="sharing-icon-circle">
                <span className="more-icon">···</span>
              </div>
              <span className="sharing-option-text">More</span>
            </div>
          </div>

          {/* Download App Section */}
          <div className="download-app-section">
            <div className="download-app-card">
              <p className="download-app-text">
                <Globe size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: '-2px' }} />
                Join 1,000s lighting Shabbat candles on time.
              </p>
              
              <div className="download-buttons-horizontal">
              <a
                href="https://apps.apple.com/us/app/yesod/id6748472269"
                target="_blank"
                rel="noopener noreferrer"
                className="download-button-simple ios-button"
                onClick={() => candleLightingMetrics.logButtonClick('download_ios')}
              >
                <div className="download-button-icon">
                  <svg viewBox="0 0 384 512" fill="currentColor" width="20" height="20">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                  </svg>
                </div>
                <div className="download-button-text">
                  <span className="download-button-label">Download on</span>
                  <span className="download-button-store">App Store</span>
                </div>
              </a>
              
              <a
                href="https://play.google.com/store/apps/details?id=us.yesod.app"
                target="_blank"
                rel="noopener noreferrer"
                className="download-button-simple android-button"
                onClick={() => candleLightingMetrics.logButtonClick('download_android')}
              >
                <div className="download-button-icon">
                  <svg viewBox="0 0 512 512" fill="currentColor" width="20" height="20">
                    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                  </svg>
                </div>
                <div className="download-button-text">
                  <span className="download-button-label">Get it on</span>
                  <span className="download-button-store">Google Play</span>
                </div>
              </a>
            </div>
            </div>
          </div>

          {/* Footer */}
          <div className="candle-lighting-footer">
            <div className="footer-links">
              <a href="/#/communities" className="footer-link">Yesod for Communities</a>
              <span className="footer-separator">•</span>
              <a href="/" className="footer-link">Yesod Tehillim Network</a>
            </div>
            <div className="footer-legal">
              <a href="/#/privacy-policy" className="footer-link">Privacy Policy</a>
              <span className="footer-separator">•</span>
              <a href="/#/terms-of-service" className="footer-link">Terms of Service</a>
            </div>
            <div className="footer-attribution">
              Halachic times powered by <a href="https://www.hebcal.com" target="_blank" rel="noopener noreferrer" className="footer-link-inline">Hebcal</a>
            </div>
            <div className="footer-copyright">
              © 2026 Yesod Inc
            </div>
          </div>
        </div>

        {/* Snackbar */}
        {showSnackbar && (
          <div className="modal-snackbar">
            <span className="modal-snackbar-text">Link copied to clipboard! ✓</span>
          </div>
        )}

        {/* Reminder Modal */}
        {showReminderModal && (
          <div className="reminder-modal-overlay" onClick={() => setShowReminderModal(false)}>
            <div className="reminder-modal" onClick={(e) => e.stopPropagation()}>
              <button className="reminder-modal-close" onClick={() => setShowReminderModal(false)}>
                <X size={24} />
              </button>
              <img 
                src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/candle-lighting-3d%20(1).png" 
                alt="Candle Lighting" 
                className="reminder-modal-icon"
              />
              <h2 className="reminder-modal-title">Turn on Shabbat Reminder</h2>
              <div className="reminder-modal-body">
                <p>We'll automatically remind you before candle lighting based on your location.</p>
                <p className="reminder-modal-highlight">
                  <Globe size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: '-2px' }} />
                  Join 1,000s lighting on time.
                </p>
              </div>
              <a
                href={getInstallAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="reminder-modal-button"
                onClick={() => {
                  // Track with internal analytics
                  candleLightingMetrics.logButtonClick('enable_in_app_modal');
                  
                  // Track with Facebook Pixel
                  trackFBEvent(FB_EVENTS.LEAD, {
                    content_name: 'Shabbat Reminder - Enable in App',
                    content_category: 'Candle Lighting',
                    source: 'reminder_modal',
                    city: shareData?.city || city,
                  });
                }}
              >
                Enable in App
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CandleLightingSharePage;
